import React, { useState, useMemo, useCallback } from 'react';
import { useGiftsData } from '../hooks/useGiftsData';
import GiftCard from './GiftCard';
import GiftBasket from './GiftBasket';

const GiftsCatalogPage = () => {
  const { giftLevelProducts, giftItems, loading, error } = useGiftsData();
  const [selectedGifts, setSelectedGifts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [budgetError, setBudgetError] = useState(null);
  const MAX_BUDGET = 600; // Default budget in BHD (will be overridden by giftLevel)

  const handleToggleGift = useCallback((gift) => {
    if (!selectedProduct || !selectedPeriod) {
      setBudgetError('Please select both product and payment period first.');
      setTimeout(() => setBudgetError(null), 3000);
      return;
    }

    setSelectedGifts((prev) => {
      const isSelected = prev.some((g) => g.id === gift.id);
      if (isSelected) {
        return prev.filter((g) => g.id !== gift.id);
      } else {
        // Check if adding this gift would exceed budget for selected period
        const currentTotal = prev.reduce((total, g) => total + (g.prices?.BHD || 0), 0);
        const giftPrice = gift.prices?.BHD || 0;
        const currentBudget = getCurrentBudget();
        if (currentTotal + giftPrice > currentBudget) {
          setBudgetError(`Cannot add this gift. Adding it would exceed ${currentBudget} BHD budget.`);
          setTimeout(() => setBudgetError(null), 3000);
          return prev;
        }
        return [...prev, gift];
      }
    });
  }, [selectedProduct, selectedPeriod]);

  const handleProductChange = useCallback((productId) => {
    setSelectedProduct(productId);
    setSelectedPeriod('');
    setSelectedGifts([]);
    setBudgetError(null);
  }, []);

  const handlePeriodChange = useCallback((period) => {
    setSelectedPeriod(period);
    // Clear all selected gifts when payment period changes
    setSelectedGifts([]);
    setBudgetError(null);
  }, []);

  // Get current budget based on selected product and period
  const getCurrentBudget = useCallback(() => {
    if (!selectedProduct || !selectedPeriod) return MAX_BUDGET;
    if (selectedPeriod === 'others') return 0; // Others option has 0 budget
    const product = giftLevelProducts.find(p => p.id === selectedProduct);
    return product?.giftLevel?.[selectedPeriod] || MAX_BUDGET;
  }, [selectedProduct, selectedPeriod, giftLevelProducts]);

  const handleRemoveGift = useCallback((gift) => {
    setSelectedGifts((prev) => prev.filter((g) => g.id !== gift.id));
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedGifts([]);
  }, []);

  const isGiftSelected = useCallback((giftId) => {
    return selectedGifts.some((g) => g.id === giftId);
  }, [selectedGifts]);

  // Calculate total value of selected gifts using useMemo
  const cartTotals = useMemo(() => ({
    USD: selectedGifts.reduce((total, gift) => total + (gift.prices?.USD || 0), 0),
    BHD: selectedGifts.reduce((total, gift) => total + (gift.prices?.BHD || 0), 0)
  }), [selectedGifts]);

  // Calculate remaining balance using useMemo
  const remainingBalance = useMemo(() => {
    const currentBudget = getCurrentBudget();
    return currentBudget - cartTotals.BHD;
  }, [getCurrentBudget, cartTotals.BHD]);

  // Only show giftItems (actual gifts) after product and period selected
  // Filter giftItems based on remaining balance
  const availableGifts = useMemo(() => {
    // Only show gift items if both product and period are selected
    if (!selectedProduct || !selectedPeriod) return [];
    
    // Don't show any gifts if "Others" is selected (0 budget)
    if (selectedPeriod === 'others') return [];
    
    return giftItems.filter((gift) => {
      const giftPrice = gift.prices?.BHD || 0;
      return giftPrice <= remainingBalance;
    });
  }, [giftItems, remainingBalance, selectedProduct, selectedPeriod]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#19BFB7]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600">Error loading gifts: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-4 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🎁 Gifts Catalog</h1>
          <p className="text-gray-600">Select a product and payment period to view available gifts</p>
        </div>

        {/* Product and Period Selection */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Product Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Product
              </label>
              <select
                value={selectedProduct || ''}
                onChange={(e) => handleProductChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19BFB7] focus:border-[#19BFB7]"
              >
                <option value="">Choose a product...</option>
                {giftLevelProducts.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Period Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Period
              </label>
              <select
                value={selectedPeriod || ''}
                onChange={(e) => handlePeriodChange(e.target.value)}
                disabled={!selectedProduct}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19BFB7] focus:border-[#19BFB7] disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">Select product first...</option>
                {selectedProduct && (() => {
                  const product = giftLevelProducts.find(p => p.id === selectedProduct);
                  if (!product?.giftLevel) return [];
                  
                  // Define order for payment periods
                  const periodOrder = ['day1', 'day30', 'day60'];
                  
                  // Get available periods and sort them
                  const availablePeriods = Object.keys(product.giftLevel)
                    .filter(key => key.startsWith('day') && product.giftLevel[key] > 0)
                    .sort((a, b) => periodOrder.indexOf(a) - periodOrder.indexOf(b))
                    .map(period => ({
                      key: period,
                      value: product.giftLevel[period],
                      label: {
                        'day1': 'Pay full within 2 days',
                        'day30': 'Pay full by end of month',
                        'day60': 'Pay full within 2 months'
                      }[period] || period
                    }));
                  
                  return availablePeriods;
                })().map(period => (
                  <option key={period.key} value={period.key}>
                    {period.label} ({period.value} BHD limit)
                  </option>
                ))}

                {/* Add Others option */}
                {selectedProduct && (
                  <option key="others" value="others">
                    Others (0 BHD limit - no gifts available)
                  </option>
                )}
              </select>
            </div>
          </div>

          {/* Selected Info */}
          {selectedProduct && selectedPeriod && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-blue-900">
                    {giftLevelProducts.find(p => p.id === selectedProduct)?.name}
                  </h4>
                  <p className="text-sm text-blue-700">
                    Budget: {getCurrentBudget()} BHD
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedProduct('');
                    setSelectedPeriod('');
                    setSelectedGifts([]);
                    setBudgetError(null);
                  }}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Budget Error Toast */}
        {budgetError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center justify-between animate-fade-in">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{budgetError}</span>
            </div>
            <button
              onClick={() => setBudgetError(null)}
              className="text-red-400 hover:text-red-600"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Gift Basket */}
        <GiftBasket
          selectedGifts={selectedGifts}
          onRemoveGift={handleRemoveGift}
          onClearAll={handleClearAll}
          cartTotalUSD={cartTotals.USD}
          cartTotalBHD={cartTotals.BHD}
          remainingBalance={remainingBalance}
          MAX_BUDGET={getCurrentBudget()}
        />

        {/* Payment Period Selection */}
        {selectedPeriod && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Selected Payment Period</h3>
                <p className="text-sm text-gray-600">
                  Budget: {getCurrentBudget()} BHD
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedPeriod('');
                  setSelectedGifts([]);
                  setBudgetError(null);
                }}
                className="text-red-500 hover:text-red-700 font-medium"
              >
                Clear Period
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        {selectedProduct && selectedPeriod && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
            <div className="text-gray-600">
              <span className="font-semibold text-gray-800">{availableGifts.length}</span> available gifts 
              (budget: {getCurrentBudget()} BHD)
            </div>
            <button
              onClick={() => window.location.reload()}
              className="text-[#19BFB7] hover:text-[#158a85] font-medium"
            >
              Refresh
            </button>
          </div>
        )}

        {/* Gifts Grid */}
        {!selectedProduct || !selectedPeriod ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <span className="text-6xl mb-4 block">🎁</span>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Select Product and Period
            </h3>
            <p className="text-gray-600">
              Please select a product and payment period to view available gifts.
            </p>
          </div>
        ) : availableGifts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <span className="text-6xl mb-4 block">🎁</span>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {selectedGifts.length > 0 ? 'Budget Exhausted' : 'No Gift Items Found'}
            </h3>
            <p className="text-gray-600">
              {selectedGifts.length > 0 
                ? `You have reached ${getCurrentBudget()} BHD budget limit. Remove some gifts to see more options.`
                : 'There are currently no gift items available within your budget.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {availableGifts.map((gift) => (
              <GiftCard
                key={gift.id}
                gift={gift}
                isSelected={isGiftSelected(gift.id)}
                onToggle={handleToggleGift}
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftsCatalogPage;
